import {
  CollectionActions,
  CollectionStateSlice,
  mergeWebsocketDocuments,
  pluckDocumentFromRawStoreCollectionState,
  Reference,
  transformIntoDocuments,
} from '@freelancer/datastore/core';
import { isDefined, toNumber } from '@freelancer/utils';
import { ReactionApi } from 'api-typings/common/common';
import { CommentsCollection } from '../comments/comments.types';
import { FeedPostsCollection } from '../feed-posts/feed-posts.types';
import {
  transformReaction,
  transformReactionToReactionTypeCountFromWS,
  transformReactionTypeCount,
  transformReactionTypeCountFromWS,
  transformReactionTypeCountId,
} from './reactions-type-count.transformers';
import { ReactionsTypeCountCollection } from './reactions-type-count.types';

export function reactionsTypeCountReducer(
  state: CollectionStateSlice<ReactionsTypeCountCollection> = {},
  action: CollectionActions<
    CommentsCollection | ReactionsTypeCountCollection | FeedPostsCollection
  >,
) {
  switch (action.type) {
    case 'API_PUSH': {
      // The component must provide a ReactionTypeCount object in order to
      // increase/decrease its count and negate the hasUserReacted property.
      if (action.payload.type === 'reactionsTypeCount') {
        const { document, ref } = action.payload;
        return mergeWebsocketDocuments<ReactionsTypeCountCollection>(
          state,
          transformIntoDocuments([document], transformReactionTypeCount),
          ref,
        );
      }
      return state;
    }

    case 'API_PUSH_ERROR': {
      // If there is an API error it reverts API_PUSH effect.
      if (action.payload.type === 'reactionsTypeCount') {
        const { document, ref } = action.payload;
        return mergeWebsocketDocuments<ReactionsTypeCountCollection>(
          state,
          transformIntoDocuments([document], transformReactionTypeCount),
          ref,
        );
      }
      return state;
    }

    case 'API_FETCH_SUCCESS': {
      // Post and comment reactions are obtained and merged in
      // from their primary resource using a projection against their API.
      if (
        action.payload.type === 'feedPosts' ||
        action.payload.type === 'comments'
      ) {
        const { result, ref } = action.payload;
        return mergeWebsocketDocuments<ReactionsTypeCountCollection>(
          state,
          transformIntoDocuments(result.reactions_count, transformReaction),
          {
            path: {
              collection: 'reactionsTypeCount',
              authUid: ref.path.authUid,
            },
          },
        );
      }
      return state;
    }

    case 'WS_MESSAGE': {
      if (action.payload.type === 'resource') {
        const { data } = action.payload;

        if (
          data.payload.updateType === 'post.react' ||
          data.payload.updateType === 'comment.react'
        ) {
          return mergeReactionWebsocketDocuments(
            state,
            action.payload.toUserId,
            true,
            data.payload.payload.reaction,
          );
        }

        if (
          data.payload.updateType === 'post.unreact' ||
          data.payload.updateType === 'comment.unreact'
        ) {
          return mergeReactionWebsocketDocuments(
            state,
            action.payload.toUserId,
            false,
            data.payload.payload.reaction,
          );
        }
      }
      return state;
    }

    default:
      return state;
  }
}

/**
 * Merges the Websocket response into the ReactionsTypeCountCollection.
 */
function mergeReactionWebsocketDocuments(
  state: CollectionStateSlice<ReactionsTypeCountCollection>,
  userId: string,
  react: boolean,
  reaction: ReactionApi,
): CollectionStateSlice<ReactionsTypeCountCollection> {
  const ref: Reference<ReactionsTypeCountCollection> = {
    path: {
      collection: 'reactionsTypeCount',
      authUid: userId,
    },
  };

  // Get the ReactionTypeCount object from the DS
  const reactionTypeCount = pluckDocumentFromRawStoreCollectionState(
    state,
    ref.path,
    transformReactionTypeCountId(
      reaction.context_type,
      reaction.context_id,
      reaction.reaction_type,
    ),
  );

  const sameUser = reaction.user_id === toNumber(userId);
  if (react && !isDefined(reactionTypeCount)) {
    // It happens when the user has created a post for example and
    // another user likes it. There won't be any document created
    // at the first client side so we have to create it.
    return mergeWebsocketDocuments<ReactionsTypeCountCollection>(
      state,
      transformIntoDocuments(
        [reaction],
        transformReactionToReactionTypeCountFromWS(sameUser),
      ),
      ref,
    );
  }

  if (
    !isDefined(reactionTypeCount) ||
    // This happens when the user is on the page, they react to something
    // and then they will receive a WS event that they reacted to which means
    // we don't have to merge it in the store because it's beean already handled.
    (sameUser && reactionTypeCount.hasUserReacted === react)
  ) {
    return state;
  }

  // Merge the document into the DS
  return mergeWebsocketDocuments<ReactionsTypeCountCollection>(
    state,
    transformIntoDocuments(
      [reactionTypeCount],
      transformReactionTypeCountFromWS(react, sameUser),
    ),
    ref,
  );
}
